import {
  Alert,
  type AlertProps,
  AlertDescription,
  AlertIcon,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { FieldErrorDto } from "../../../autogen/bff-api";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";

const DOFFIN_REFUSALS = [
  "DOFFIN_SCHEMATRON_FAILED",
  "DOFFIN_VALIDATION_FAILED",
];

export const doffinRefusal = (e: unknown): FieldErrorDto[] | null =>
  isFetchBaseQueryError(e) &&
  isApiErrorDto(e.data) &&
  DOFFIN_REFUSALS.includes(e.data.errorCode)
    ? (e.data.fieldErrors ?? [])
    : null;

export const DoffinRefusalAlert = ({
  breaches,
  heading,
  advice,
  ...alertProps
}: {
  breaches: FieldErrorDto[];
  heading?: ReactNode;
  advice?: ReactNode;
} & AlertProps) => {
  const { t } = useTranslation();
  const describe = (breach: FieldErrorDto) => {
    if (breach.code !== "DOFFIN_SCHEMATRON_ERROR")
      return t(breach.code, breach.params ?? {});
    return breach.params?.identifier
      ? t("DOFFIN_SCHEMATRON_ERROR", breach.params)
      : (breach.params?.text ?? breach.field);
  };
  return (
    <Alert status="error" alignItems="start" {...alertProps}>
      <AlertIcon />
      <AlertDescription>
        <Text>{heading ?? t("Doffin refused the result notice. Nothing has been announced.")}</Text>
        {breaches.length > 0 && (
          <UnorderedList mt="2">
            {breaches.map((breach, index) => (
              <ListItem
                key={`${breach.params?.identifier ?? breach.field}-${index}`}
              >
                {describe(breach)}
              </ListItem>
            ))}
          </UnorderedList>
        )}
        <Text mt="2">
          {advice ?? t("Nothing in this dialog can correct this. Contact support.")}
        </Text>
      </AlertDescription>
    </Alert>
  );
};

const DPS_SCHEME_NOTICE_MISSING = "zz_DoffinApiErrorCode_DpsSchemeNoticeMissing";
const RESULT_NOTICE_ALREADY_SENT = "zz_SourcingEventApiErrorCode_ResultNoticeAlreadySent";

export const isResultNoticeAlreadySent = (e: unknown): boolean =>
  e != null && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode === RESULT_NOTICE_ALREADY_SENT;

export const isDpsSchemeNoticeMissing = (e: unknown): boolean =>
  e != null && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode === DPS_SCHEME_NOTICE_MISSING;
